import AssetSmallView from '../../../../AssetSmallView/AssetSmallView';
import InfiniteScrollTable from '../../../../Table/InfiniteScrollTable';
import { Truncate } from '../../../../Truncate/Truncate';
import React, { useState, useCallback, useEffect, useRef, FC } from 'react';
import { isEmpty } from 'lodash';
import { TextClipBoard, TwoLayerTextDisplay } from '../../ModuleCell';
import { EmptyFolderObj, MoveTargetEmptyFolder, MoveTargetNewFolder, UploadTargetEmptyFolder, UploadTargetNewFolder, NoAssetsFound, NoResultEmptyObj, getEmptyStateWithActions } from '../EmptyState';
import { fetchSpaceAssetsData } from '../viewHelper';
import {
  SPACE_ASSETS_VIEW_TYPE,
} from '../../../utils/constants';
import AssetScanStatusPill from '../../common/AssetScanStatusPill';
import { getAssetType } from '../viewHelper';
import '../viewComponent.scss';

export const getAssetDimension = (asset) => {
  if (asset['dimension.width'] && asset['dimension.height']) {
    return {
      height: asset['dimension.height'],
      width: asset['dimension.width']
    }
  }
  return asset.dimension;
}

import {
  initializeStatusMap,
  updateItemStatus,
  convertSortFormat,
  TagDisplay
} from './ListViewHelper';

import { AssetData, ListViewProps } from './ListViewHelper';
import { AssetData as AssetDataProps } from '../../../utils/types';
import { buildSecureAssetDeliveryUrl, isPrivateAssetAccess } from '../../../../../utils/secureAssetDeliveryUrl';
import AssetPickerAccessBadge from './AssetPickerAccessBadge';
import { useQueryBuilder } from '../../SpaceAssetsSelectorModal/context/QueryBuilderContext';
import { extractTextFromJSX } from '../../SpaceAssetsSelectorModal/helper';

const ListView: FC<ListViewProps> = (props) => {
  const {
    isMultiple,
    maxInstance,
    spaceAssetsPicked,
    setSpaceAssetsPicked,
    breadcrumbClickHandler,
    selectedFolder,
    spaceAssetsSearchOptions,
    setMessageComponent,
    setIsAPIError,
    isAPIError,
    linkedSpaces = [],
    setShouldClearSearchFilters,
    handleGoBack,
    setShouldDisabledActionPanel,
    totalCounts,
    setTotalCounts,
    showSelectedAssets,
    ai_suggestions,
    linkedWorkspaces = [],
    fetchSpaceAssets,
    validationFilter,
    disablePrivateAssets = false,
    assetPrivacyEnabled = false,
    spacesWithTitles = [],
    isMoveTarget = false,
    isUploadTarget = false,
    isNewlyCreatedFolder = false,
    getUserDisplayName,
    onSuggestionsFetchSettled,
    userDisplayNameMap = {},
    assetScanEnabled = false,
  } = props;
  const [loading, setLoading] = useState<boolean>(showSelectedAssets ? false : true);
  const [assetData, setAssetData] = useState<AssetData[]>(showSelectedAssets ? spaceAssetsPicked : []);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(showSelectedAssets ? false : true);
  const constructSelectedAssetsMap = useCallback((dataCount: number) => {
    const selectedAssetsMap: Record<number, string> = {};
    for (let i = 0; i < dataCount; i++) {
      selectedAssetsMap[i] = 'loaded';
    }
    return selectedAssetsMap;
  }, [showSelectedAssets, spaceAssetsPicked]);
  const [itemStatusMap, setItemStatusMap] = useState<Record<number, string>>((showSelectedAssets && spaceAssetsPicked.length ? constructSelectedAssetsMap(spaceAssetsPicked.length) : {}));
  const [startIndex, setStartIndex] = useState<number>(0);
  const [stopIndex, setStopIndex] = useState<number>(29);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isSortChanged, setIsSortChanged] = useState<boolean>(false);

  const [tableSortBy, setTableSortBy] = useState<Array<{ id: string; desc: boolean }>>([{
    id: 'title',
    desc: false
  }]);

  const initialDataLoaded = useRef<boolean>(false);
  const isRequestInProgressRef = useRef<boolean>(false);
  const hasInitialDataRef = useRef<boolean>(false);
  // One AbortController per query context — a fresh query (skip 0) aborts the prior
  // one so in-flight Lottie sniffs cancel on query/search/sort change; paging reuses it.
  const lottieAbortRef = useRef<AbortController | null>(null);

  const { queryBuilder, setCondition } = useQueryBuilder();

  const v2Features = { canFreezeCheckbox: true, isNewEmptyState: true };
  const v2FeaturesSingle = { isNewEmptyState: true };

  const initialSelectedRowIdsMap: Record<string, boolean> = {};
  if (isMultiple) {
    spaceAssetsPicked.forEach((obj: AssetData) => {
      initialSelectedRowIdsMap[obj.uid] = true;
    });
  }

  const getSpaceName = (spaceUid?: string): string => {
    if (!spaceUid || !spacesWithTitles.length) return '';
    const space = spacesWithTitles.find(s => s.uid === spaceUid);
    return space?.title || '';
  };

  const formatAssetPath = (pathArray: Array<{ title: string; uid: string }> | undefined, spaceUid?: string) => {
    const spaceName = getSpaceName(spaceUid);
    const spacePrefix = spaceName ? `/${spaceName}` : '';
    if (!pathArray || pathArray.length === 0) {
      return spacePrefix ? `${spacePrefix}/` : '/..';
    }
    return `${spacePrefix}/` + pathArray.map(p => p.title).join('/') + '/';
  }

  const columns = [
    {
      Header: 'Title',
      id: 'title',
      accessor: (data: AssetData): JSX.Element => {
        const scanStatus = data._asset_scan_status;
        const accessType = (data as never as { settings?: { privacy_settings?: { asset_access?: string } } })
          ?.settings?.privacy_settings?.asset_access;
        const showAccessBadge =
          !data.is_dir && assetPrivacyEnabled && (accessType === 'public' || accessType === 'private');

        return (
          <div className="flex cs-asset-picker-title-with-access">
            <AssetSmallView
              type={getAssetType(data)}
              dimensions={getAssetDimension(data)}
              url={buildSecureAssetDeliveryUrl(data.url, { isPrivate: isPrivateAssetAccess(data as never) })}
              isLottie={data.isLottie}
              name={data.name || data.title}
              version="v2"
              testId={`cs-asset-table-title-${data.title}`}
              assetScanStatus={scanStatus}
            />
            {showAccessBadge && (
              <AssetPickerAccessBadge
                mode={accessType === 'private' ? 'private' : 'public'}
                spaceName={getSpaceName(data.space_uid)}
                position="right"
              />
            )}
          </div>
        );
      },
      canDragDrop: true,
      width: 350,
      cssClass: 'titleCell',
      addToColumnSelector: true,
      disableSortBy: showSelectedAssets,
      disableResizing: false
    },
    {
      Header: 'File Path',
      id: 'path',
      accessor: (data: AssetData): JSX.Element => {
        const path = formatAssetPath(data.path as Array<{ title: string; uid: string }>, data.space_uid);
        return (
          <Truncate
            isResponsive
            truncateFrom="end"
            testId={`cs-asset-table-path-${data.uid}`}
          >
            {path}
          </Truncate>
        );
      },
      canDragDrop: true,
      width: 250,
      cssClass: 'filePathCell',
      addToColumnSelector: true,
      disableSortBy: true,
      disableResizing: false
    },
    {
      Header: 'Language',
      id: 'locale',
      accessor: (data: AssetData): JSX.Element => {
        const languageName = props?.localesWithFallback?.find((locale: any) => locale?.code === data?.locale)?.name || '-';
        return (
          <Truncate
            isResponsive
            truncateFrom="end"
            testId={`cs-asset-table-language-${data?.locale}`}
          >
            {languageName}
          </Truncate>
        );
      },
      canDragDrop: true,
      width: 250,
      cssClass: 'assetTypeCell',
      addToColumnSelector: true,
      disableSortBy: showSelectedAssets,
      disableResizing: false
    },
    ...(assetScanEnabled
      ? [{
        Header: 'Scan Status',
        id: '_asset_scan_status',
        accessor: (data: AssetData): JSX.Element => {
          const scanStatus = data._asset_scan_status;
          return <AssetScanStatusPill isDir={data.is_dir} assetScanStatus={scanStatus} />;
        },
        canDragDrop: true,
        disableSortBy: showSelectedAssets,
        addToColumnSelector: true,
        width: 200,
        minWidth: 200,
        cssClass: 'assetScanStatusCell',
        disableResizing: false
      }]
      : []),
    {
      Header: 'Asset Type',
      id: 'contentType',
      accessor: (data: AssetData): JSX.Element => {
        const isFolder = !!data.is_dir || !!(data as Record<string, any>).isDir;
        return (
          <div
            style={{ textTransform: 'capitalize' }}
            data-test-id={`cs-asset-table-asset-type-${isFolder ? 'folder' : data?.asset_type?.title || 'file'}`}
          >
            {isFolder ? 'Folder' : data?.asset_type?.title || 'File'}
          </div>
        );
      },
      addToColumnSelector: true,
      canDragDrop: true,
      width: 200,
      cssClass: 'assetTypeCell',
      disableSortBy: showSelectedAssets,
      disableResizing: false
    },
    {
      Header: 'Space',
      id: 'space',
      accessor: (data: AssetData): JSX.Element => {
        const spaceName = getSpaceName(data.space_uid) || '-';
        if (spaceName === '-') {
          return (
            <div className="emptyField" data-test-id={'cs-asset-table-space-name'}>
              -
            </div>
          );
        }
        return (
          <Truncate
            isResponsive
            truncateFrom="end"
            testId={`cs-asset-table-space-${spaceName}`}
          >
            {spaceName}
          </Truncate>
        );
      },
      canDragDrop: true,
      addToColumnSelector: true,
      disableSortBy: true,
      cssClass: 'spaceNameCell',
      width: 350,
      disableResizing: false
    },
    {
      Header: 'Description',
      id: 'description',
      accessor: (data: AssetData): JSX.Element => {
        const description = data.description || '-';
        if (description === '-') {
          return (
            <div className="emptyField" data-test-id={'cs-asset-table-description'}>
              -
            </div>
          );
        }
        return (
          <Truncate
            isResponsive
            truncateFrom="end"
            testId={`cs-asset-table-desciption-${data?.description}`}
          >
            {description}
          </Truncate>
        );
      },
      canDragDrop: true,
      addToColumnSelector: true,
      disableSortBy: true,
      cssClass: 'descriptionCell',
      width: 350,
      disableResizing: false
    },
    {
      Header: 'URL',
      id: 'url',
      accessor: (data: AssetData): JSX.Element => {
        const url = data?.url || '';
        return (
          <>
            {url ? (
              <TextClipBoard
                testId={`cs-asset-table-url-${data?.url}`}
                copyLabel={'url'}
                text={url}
              />
            ) : (
              <div className="emptyField" data-test-id={'cs-asset-table-url'}>
                -
              </div>
            )}
          </>
        );
      },
      canDragDrop: true,
      addToColumnSelector: true,
      disableSortBy: true,
      cssClass: 'urlCell',
      width: 500,
      disableResizing: false
    },
    {
      Header: 'Unique ID',
      id: 'uid',
      accessor: (data: AssetData): JSX.Element => {
        return (
          <TextClipBoard
            copyLabel={'Unique Id'}
            text={data?.uid}
            testId={`cs-asset-table-uid-${data?.uid}`}
          />
        );
      },
      addToColumnSelector: true,
      disableSortBy: true,
      canDragDrop: true,
      width: 250,
      minWidth: 250,
      cssClass: 'uniqueIdCell',
      disableResizing: false
    },
    {
      Header: 'Tags',
      id: 'tags',
      accessor: (data: AssetData): JSX.Element => {
        const tags = data?.tags || [];
        return <TagDisplay tags={tags} label="Tag" testId="cs-asset-table-tag" />;
      },
      addToColumnSelector: true,
      disableSortBy: true,
      cssClass: 'tagCell',
      canDragDrop: true,
      width: 170,
      disableResizing: false
    },
    {
      Header: 'Modified At',
      id: 'updated_at',
      accessor: (data: AssetData): JSX.Element => {
        const updatedAt = data?.updated_at ?? (data as any)?.updatedAt;
        const updatedBy = data?.updated_by ?? (data as any)?.updatedBy;

        const displayName = (data as any)?.updated_by_name ||
          (data as any)?.updatedByName ||
          (getUserDisplayName && typeof updatedBy === 'string' ? getUserDisplayName(updatedBy) : (typeof updatedBy === 'string' ? updatedBy : '-'));

        return (
          <TwoLayerTextDisplay
            isPrimaryTextWithDate
            primaryText={data?.updated_at}
            secondaryText={userDisplayNameMap?.[data?.updated_by] || data?.updated_by || '-'}
            testId={`cs-asset-table-updated-at-${data?.updated_at}`}
          />
        );
      },
      canDragDrop: true,
      addToColumnSelector: true,
      cssClass: 'lastModifiedCell',
      width: 250,
      disableResizing: false
    },
    {
      Header: 'Created At',
      id: 'created_at',
      accessor: (data: AssetData): JSX.Element => {
        const createdAt = data?.created_at ?? (data as any)?.createdAt;
        const createdBy = data?.created_by ?? (data as any)?.createdBy;

        const displayName = (data as any)?.created_by_name ||
          (data as any)?.createdByName ||
          (getUserDisplayName && typeof createdBy === 'string' ? getUserDisplayName(createdBy) : (typeof createdBy === 'string' ? createdBy : '-'));

        return (
          <TwoLayerTextDisplay
            isPrimaryTextWithDate
            primaryText={data?.created_at}
            secondaryText={userDisplayNameMap?.[data?.created_by] || data?.created_by || '-'}
            testId={`cs-asset-table-created-at-${data?.created_at}`}
          />
        );
      },
      canDragDrop: true,
      addToColumnSelector: true,
      cssClass: 'createdAtCell',
      width: 250,
      disableResizing: false
    },
  ];

  const fetchData = useCallback(
    ({ skip, limit, sortBy: sortByParam, startIndex, stopIndex }: {
      skip: number;
      limit: number;
      sortBy?: Array<{ id: string; desc: boolean }>;
      startIndex: number;
      stopIndex: number;
    }) => {
      if (showSelectedAssets) return;

      if (startIndex === 0 && !isLoadingMore && hasInitialDataRef.current) {
        return Promise.resolve();
      }

      if (isRequestInProgressRef.current && startIndex === 0 && !isLoadingMore) {
        return Promise.resolve();
      }

      isRequestInProgressRef.current = true;

      let sortChanged = false;
      if (sortByParam && Array.isArray(sortByParam) && sortByParam.length > 0) {
        const currentSort = tableSortBy[0];
        const newSort = sortByParam[0];

        sortChanged = currentSort.id !== newSort.id || currentSort.desc !== newSort.desc;

        if (sortChanged) {
          setTableSortBy(sortByParam);
          setIsSortChanged(true);
        }
      }

      updateItemStatus(startIndex, stopIndex, 'loading', setItemStatusMap);

      const actualSkip = sortChanged ? 0 : startIndex;
      const actualLimit = stopIndex - startIndex + 1;

      if (actualSkip === 0) {
        lottieAbortRef.current?.abort();
        lottieAbortRef.current = new AbortController();
      }

      const apiSortBy = convertSortFormat(sortByParam || tableSortBy);

      const dataArray = (sortChanged || isSortChanged) ? [] : assetData;

      const viewType = isLoadingMore && !sortChanged ?
        SPACE_ASSETS_VIEW_TYPE.THUMBNAIL :
        SPACE_ASSETS_VIEW_TYPE.LIST;


      return fetchSpaceAssetsData(
        queryBuilder,
        {
          sortBy: apiSortBy,
          searchText: spaceAssetsSearchOptions.searchText,
          searchType: extractTextFromJSX(spaceAssetsSearchOptions.searchType, 1),
          skip: actualSkip,
          limit: actualLimit,
          ai_suggestions,
          startIndex,
          stopIndex,
        },
        dataArray as AssetDataProps[],
        (loadingState: boolean) => {
          isRequestInProgressRef.current = false;
          setLoading(loadingState);
          setStartIndex(startIndex);
          setStopIndex(stopIndex);

          if (!loadingState) {
            setIsInitialLoad(false);
            setIsLoadingMore(false);

            hasInitialDataRef.current = true;
            initialDataLoaded.current = true;

            if (sortChanged) {
              setTimeout(() => {
                setIsSortChanged(false);
              }, 100);
            }
          }
        },
        (newData: AssetDataProps[]) => {
          if (sortChanged || isSortChanged) {
            setAssetData(newData as AssetData[]);
          } else if (isLoadingMore) {
            setAssetData(prevData => [...prevData, ...newData] as AssetData[]);
          } else if (startIndex === 0) {
            setAssetData(newData as AssetData[]);
          } else {
            setAssetData(prevData => [...prevData, ...newData] as AssetData[]);
          }
        },
        setTotalCounts,
        setIsAPIError,
        setShouldDisabledActionPanel,
        fetchSpaceAssets,
        viewType,
        linkedWorkspaces,
        (startIdx: number, stopIdx: number, status: 'loading' | 'loaded') =>
          updateItemStatus(startIdx, stopIdx, status, setItemStatusMap),
        null,
        validationFilter,
        onSuggestionsFetchSettled,
        disablePrivateAssets,
        lottieAbortRef.current?.signal
      );
    },
    [
      assetData,
      linkedSpaces,
      isLoadingMore,
      isSortChanged,
      tableSortBy,
      queryBuilder,
      ai_suggestions,
      linkedWorkspaces,
      validationFilter,
      onSuggestionsFetchSettled,
      disablePrivateAssets
    ]
  );

  const onRowClick = useCallback((data: AssetData) => {
    if (data.invalid_asset) {
      return;
    }

    const isFolder = !!data.is_dir || !!(data as any).isDir;
    if (isFolder) {
      const crumbs = { name: data.title, root: false, folder_uid: data.uid, ...data };
      breadcrumbClickHandler(crumbs);
    } else {
      setSpaceAssetsPicked((prevSpaceAssetsPicked) => {
        const isAlreadyPicked = prevSpaceAssetsPicked.some((pAsset: AssetData) => pAsset.uid === data.uid);

        if (!isMultiple) {
          return [data];
        } else {
          if (!isAlreadyPicked) {
            return [...prevSpaceAssetsPicked, data];
          } else {
            return prevSpaceAssetsPicked.filter((pAsset: AssetData) => pAsset.uid !== data.uid);
          }
        }
      });
    }
  }, [breadcrumbClickHandler, isMultiple, setSpaceAssetsPicked]);

  const getSelectedRowCallBack = useCallback((selectedUids: string[], allData: AssetData[]): void => {
    if (!isMultiple) return;

    if (!Array.isArray(selectedUids) || !Array.isArray(allData)) return;

    const selectedAssets: AssetData[] = [];

    selectedUids.forEach((uid: string) => {
      const asset = allData.find(item => item.uid === uid && !item.is_dir && !item.invalid_asset);
      if (asset) {
        selectedAssets.push(asset);
      }
    });

    if (selectedAssets.length > 0 || selectedUids.length === 0) {
      setSpaceAssetsPicked(selectedAssets);
    }
  }, [isMultiple, setSpaceAssetsPicked]);

  const loadMoreItems = useCallback(
    async ({ skip, limit, startIndex, stopIndex }: {
      skip: number;
      limit: number;
      startIndex: number;
      stopIndex: number;
    }) => {
      if (showSelectedAssets) return;
      if (isLoadingMore) return Promise.resolve();

      setIsLoadingMore(true);

      try {
        updateItemStatus(startIndex, stopIndex, 'loading', setItemStatusMap);

        await fetchData({
          skip: startIndex,
          limit: stopIndex - startIndex + 1,
          sortBy: tableSortBy,
          startIndex,
          stopIndex
        });

      } catch (error) {
        updateItemStatus(startIndex, stopIndex, 'loaded', setItemStatusMap);
        setIsLoadingMore(false);
        isRequestInProgressRef.current = false;
      }
    },
    [fetchData, tableSortBy, isLoadingMore]
  );

  const handleSortByChange = useCallback((newSortBy) => {
    if (showSelectedAssets) return;

    setTableSortBy(newSortBy);
    setIsSortChanged(true);

    hasInitialDataRef.current = false;

    setIsInitialLoad(true);
    setIsLoadingMore(false);
    setLoading(true);

    initializeStatusMap(0, 29, 'loading', setItemStatusMap);

    fetchData({
      skip: 0,
      limit: 30,
      sortBy: newSortBy,
      startIndex: 0,
      stopIndex: 29
    });
  }, [fetchData]);

  useEffect(() => {
    if (!showSelectedAssets) {
      hasInitialDataRef.current = false;
      isRequestInProgressRef.current = false;

      setLoading(true);
      setIsInitialLoad(true);
      setIsSortChanged(false);
      setIsLoadingMore(false);

      initializeStatusMap(0, 29, 'loading', setItemStatusMap);

      fetchData({
        skip: 0,
        limit: 30,
        sortBy: tableSortBy,
        startIndex: 0,
        stopIndex: 29
      });
    }

    return () => {
      isRequestInProgressRef.current = false;
      // Cancel any in-flight Lottie sniff on query change or when the view unmounts.
      lottieAbortRef.current?.abort();
    };
  }, [linkedSpaces, queryBuilder, ai_suggestions]);

  const handleRetryFetch = useCallback(() => {
    if (showSelectedAssets) return;
    isRequestInProgressRef.current = false;

    setLoading(true);
    setIsInitialLoad(true);
    setIsSortChanged(false);
    setIsLoadingMore(false);
    initializeStatusMap(0, 29, 'loading', setItemStatusMap);

    fetchData({
      skip: 0,
      limit: 30,
      sortBy: tableSortBy,
      startIndex: 0,
      stopIndex: 29
    });
  }, [fetchData, tableSortBy]);

  const handlers = {
    handleGoBack: handleGoBack,
    handleRetryFetch: handleRetryFetch,
    handleClearFilters: (): void => {
      if (setShouldClearSearchFilters) {
        setShouldClearSearchFilters(true);
      }
    }
  };

  const getFolderPickerEmptyObj = () => {
    if (isUploadTarget) {
      return isNewlyCreatedFolder ? UploadTargetNewFolder : UploadTargetEmptyFolder;
    }
    return isNewlyCreatedFolder ? MoveTargetNewFolder : MoveTargetEmptyFolder;
  };

  const baseEmptyObj = isAPIError
    ? NoAssetsFound
    : (!isEmpty(spaceAssetsSearchOptions.searchText)
      ? NoResultEmptyObj
      : (isMoveTarget ? getFolderPickerEmptyObj() : EmptyFolderObj));

  const currentEmptyObj = getEmptyStateWithActions(baseEmptyObj, handlers);

  const displayData = showSelectedAssets && spaceAssetsPicked.length ? spaceAssetsPicked : assetData;

  // In moveTarget mode, show only folder-relevant columns
  const displayColumns = isMoveTarget
    ? columns.filter(col => ['title', 'locale', 'contentType', 'updated_at'].includes(col.id))
    : columns;

  return (
    <div className={`list-view-wrapper ${!isMultiple ? "single-select-wrapper" : ""}`}>
      <InfiniteScrollTable
        fetchTableData={fetchData}
        itemStatusMap={itemStatusMap}
        columns={displayColumns}
        showSelected={showSelectedAssets}
        isRowSelect={isMoveTarget ? false : isMultiple}
        data={displayData}
        uniqueKey={'uid'}
        columnSelector={false}
        loading={loading || isInitialLoad}
        totalCounts={showSelectedAssets ? spaceAssetsPicked.length : totalCounts}
        loadMoreItems={loadMoreItems}
        initialSelectedRowIds={isMultiple && !isMoveTarget ? initialSelectedRowIdsMap : undefined}
        singleSelectedRowId={!isMultiple && !isMoveTarget ? (spaceAssetsPicked.length > 0 && spaceAssetsPicked[0].uid) : undefined}
        onRowClick={onRowClick}
        rowSelectCheckboxProp={isMoveTarget ? undefined : {
          key: 'is_dir',
          value: !isMultiple
        }}
        maxSelect={isMultiple && !isMoveTarget ? maxInstance : undefined}
        fullRowSelect={isMultiple && !isMoveTarget}
        v2Features={isMultiple ? { ...v2FeaturesSingle, ...v2Features } : v2FeaturesSingle}
        hideTablePanel={true}
        emptyObj={currentEmptyObj}
        initialSortBy={tableSortBy}
        onSortByChange={handleSortByChange}
        testId="am-v2-asset-picker-table-list"
        getSelectedRow={isMoveTarget ? undefined : getSelectedRowCallBack}
        isResizable={true}
        tableWidth={200}
        rowDisableProp={{ key: 'invalid_asset', value: true }}
        onRowHoverText={(data: any): any => data.errorMessage?.trim()}
      />
    </div>
  );
};

export default ListView;